import { useMemo, useState } from "react";
import { useGame } from "../services/gameContext";
import GamePicker from "../components/GamePicker";
import { POP_POINT_TABLE } from "../components/PopBattlePointChart";

const ACCENTS = { BGMI: "#F88200", PUBG: "#258BC8" };
const WIN_COLOR = "#3ddc84";
const LOSE_COLOR = "#ff6b6b";
const MONO = "'JetBrains Mono', ui-monospace, 'SF Mono', Menlo, monospace";
const NUMFONT = "'Rajdhani', system-ui, -apple-system, sans-serif";

// ---------- pure helpers ----------

// "12k" / "1.2m" / "12,000" / "12000" → integer popularity (null = unparsable)
function parsePop(raw) {
  if (raw == null) return null;
  const s = String(raw).trim().toLowerCase().replace(/,/g, "");
  if (!s) return null;
  const m = s.match(/^(\d+(?:\.\d+)?)\s*([km])?$/);
  if (!m) return null;
  let v = parseFloat(m[1]);
  if (m[2] === "k") v *= 1e3;
  else if (m[2] === "m") v *= 1e6;
  return Math.round(v);
}

// Clean display: 2499 shows as its tier edge 2.5K (tiers are
// top-exclusive; the −1 only keeps table ranges non-overlapping).
function fmtPop(n) {
  if (n == null) return "—";
  const clean = n % 10 === 9 ? n + 1 : n;
  if (clean >= 1e6) return `${+(clean / 1e6).toFixed(2)}M`;
  if (clean >= 1e3) return `${+(clean / 1e3).toFixed(1)}K`;
  return String(clean);
}

function tierIndexOf(pop) {
  if (pop == null || pop < 0) return -1;
  return POP_POINT_TABLE.findIndex(
    (t) => pop >= t.from && (t.to === null || pop <= t.to),
  );
}

function tierRangeLabel(t) {
  return t.to === null ? `${fmtPop(t.from)}+` : `${fmtPop(t.from === 0 ? 0 : t.from)} – ${fmtPop(t.to)}`;
}

// Target-finder rows show the minimum pop that unlocks the tier ("120K"),
// not the full range.
function minPopLabel(t) {
  return t.from === 0 ? "0" : fmtPop(t.from);
}

// ---------- small UI atoms ----------

function Card({ accent, children, style }) {
  return (
    <div
      style={{
        borderRadius: 18,
        border: `1px solid ${accent}44`,
        background: `linear-gradient(160deg, ${accent}12 0%, rgba(255,255,255,0.02) 40%, rgba(0,0,0,0) 100%), #0e1218`,
        boxShadow: `0 20px 50px rgba(0,0,0,0.55), 0 0 40px ${accent}22`,
        padding: "18px 18px 20px",
        ...style,
      }}
    >
      {children}
    </div>
  );
}

// Clickable 12-segment tier strip; the active tier glows.
function TierBar({ accent, activeIdx, onPick }) {
  return (
    <div style={{ display: "flex", gap: 3, marginTop: 10 }}>
      {POP_POINT_TABLE.map((t, i) => {
        const active = i === activeIdx;
        return (
          <button
            key={t.points}
            onClick={() => onPick(t.from === 0 ? "1,000" : t.from.toLocaleString("en-US"))}
            title={`${tierRangeLabel(t)} popularity → ${t.points} pts`}
            style={{
              flex: 1, minWidth: 0, height: 30, borderRadius: 6, cursor: "pointer",
              border: active ? `1px solid ${accent}` : "1px solid rgba(255,255,255,0.08)",
              background: active
                ? `linear-gradient(135deg, ${accent}, ${accent}99)`
                : "rgba(255,255,255,0.04)",
              color: active ? "#0d1017" : "#8f93a0",
              fontFamily: NUMFONT, fontWeight: 700, fontSize: 13, lineHeight: 1,
              boxShadow: active ? `0 0 14px ${accent}66` : "none",
              padding: 0,
              transition: "all 0.15s ease",
            }}
          >
            {t.points}
          </button>
        );
      })}
    </div>
  );
}

// One side of the matchup: input + tier bar + tier readout + next-tier lap.
function PopSide({ accent, label, sub, value, onChange }) {
  const pop = parsePop(value);
  const idx = tierIndexOf(pop);
  const tier = idx >= 0 ? POP_POINT_TABLE[idx] : null;
  const next = idx >= 0 && idx < POP_POINT_TABLE.length - 1 ? POP_POINT_TABLE[idx + 1] : null;
  const need = next ? tier.to + 1 - pop : null;
  const progress = tier
    ? tier.to === null ? 1 : Math.min(1, (pop - tier.from) / (tier.to - tier.from))
    : 0;

  return (
    <div style={{ flex: "1 1 260px", minWidth: 0 }}>
      <div style={{ fontSize: 11.5, fontWeight: 900, letterSpacing: 1.8, color: accent, marginBottom: 2 }}>
        {label}
      </div>
      <div style={{ fontSize: 11, color: "#7a7d86", marginBottom: 8 }}>{sub}</div>

      <input
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder="e.g. 12k, 1.2m, 48000"
        inputMode="text"
        style={{
          width: "100%", boxSizing: "border-box",
          padding: "12px 14px", borderRadius: 12,
          background: "rgba(0,0,0,0.35)",
          border: `1px solid ${tier ? accent + "66" : "rgba(255,255,255,0.12)"}`,
          color: "#fff", fontSize: 20, fontWeight: 700, fontFamily: NUMFONT,
          letterSpacing: 1, outline: "none",
        }}
      />

      <TierBar accent={accent} activeIdx={idx} onPick={onChange} />

      {tier ? (
        <>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", marginTop: 10 }}>
            <span style={{ fontFamily: MONO, fontSize: 12, color: "#a4a7b0" }}>
              TIER {tierRangeLabel(tier)}
            </span>
            <span style={{ fontFamily: NUMFONT, fontSize: 24, fontWeight: 700, color: accent, lineHeight: 1 }}>
              {tier.points}<span style={{ fontSize: 12, color: "#a4a7b0", marginLeft: 3 }}>pts</span>
            </span>
          </div>

          {/* position inside the tier */}
          <div style={{ height: 5, borderRadius: 99, background: "rgba(255,255,255,0.07)", marginTop: 7, overflow: "hidden" }}>
            <div style={{ height: "100%", width: `${Math.max(4, progress * 100)}%`, borderRadius: 99, background: `linear-gradient(90deg, ${accent}88, ${accent})` }} />
          </div>

          <div
            style={{
              marginTop: 9, padding: "8px 11px", borderRadius: 10,
              background: "rgba(255,255,255,0.03)", border: "1px solid rgba(255,255,255,0.07)",
              fontSize: 12, color: "#c9cdd6", lineHeight: 1.55,
            }}
          >
            {next ? (
              <>
                <span style={{ color: accent, fontWeight: 800 }}>▲ +{fmtPop(need)} pop</span>
                {" → next tier "}
                <span style={{ fontFamily: NUMFONT, fontWeight: 700 }}>
                  {tier.points} → {next.points} pts
                </span>{" "}
                <span style={{ color: WIN_COLOR, fontWeight: 700 }}>(+{next.points - tier.points})</span>
              </>
            ) : (
              <span style={{ color: "#ffd166", fontWeight: 800 }}>★ MAX TIER — highest base points in the game</span>
            )}
          </div>
        </>
      ) : (
        <div style={{ marginTop: 10, fontSize: 12, color: "#7a7d86" }}>
          Enter a popularity to see the tier — or tap a segment above.
        </div>
      )}
    </div>
  );
}

// Single-row result: badge + explanation + formula on the left, big
// settled number pinned right. Replaces the old card grid — with only
// one outcome shown, a card stretched full-width and looked empty.
function ResultStrip({ color, badge, text, formula, value }) {
  return (
    <div
      style={{
        display: "flex", alignItems: "center", gap: 14, flexWrap: "wrap",
        padding: "12px 16px", borderRadius: 13,
        background: `linear-gradient(90deg, ${color}16, rgba(0,0,0,0.22) 70%)`,
        border: `1px solid ${color}4D`,
      }}
    >
      <span
        style={{
          fontSize: 10.5, fontWeight: 900, letterSpacing: 1.6, color,
          background: `${color}1F`, border: `1px solid ${color}55`,
          padding: "5px 11px", borderRadius: 999, whiteSpace: "nowrap",
        }}
      >
        {badge}
      </span>
      <div style={{ flex: "1 1 180px", minWidth: 0 }}>
        <div style={{ fontSize: 12.5, color: "#c9cdd6" }}>{text}</div>
        <div style={{ fontFamily: MONO, fontSize: 11, color: "#8f93a0", marginTop: 3 }}>{formula}</div>
      </div>
      <div style={{ display: "flex", alignItems: "baseline", gap: 6, marginLeft: "auto" }}>
        <span style={{ fontFamily: NUMFONT, fontSize: 34, fontWeight: 700, color: "#fff", lineHeight: 1, textShadow: `0 0 18px ${color}66` }}>
          {value}
        </span>
        <span style={{ fontSize: 12, color: "#a4a7b0" }}>pts</span>
      </div>
    </div>
  );
}

// ---------- forward mode ----------

function ForwardCalc({ accent, game }) {
  const [you, setYou] = useState("50k");
  const [opp, setOpp] = useState("15k");
  const yourPop = parsePop(you);
  const oppPop = parsePop(opp);
  const yi = tierIndexOf(yourPop);
  const oi = tierIndexOf(oppPop);
  const A = yi >= 0 ? POP_POINT_TABLE[yi].points : null;
  const B = oi >= 0 ? POP_POINT_TABLE[oi].points : null;
  const ready = A != null && B != null;

  const oppNext = oi >= 0 && oi < POP_POINT_TABLE.length - 1 ? POP_POINT_TABLE[oi + 1] : null;

  return (
    <Card accent={accent}>
      <div style={{ display: "flex", gap: 20, flexWrap: "wrap", alignItems: "stretch" }}>
        <PopSide
          accent={accent}
          label="YOUR POPULARITY"
          sub="Your base points come from this"
          value={you}
          onChange={setYou}
        />
        <div
          style={{
            alignSelf: "center", flexShrink: 0,
            fontFamily: NUMFONT, fontSize: 20, fontWeight: 700, letterSpacing: 2,
            color: "#565b68", padding: "0 2px",
          }}
        >
          VS
        </div>
        <PopSide
          accent={accent}
          label="OPPONENT POPULARITY"
          sub="You loot half their points on a win"
          value={opp}
          onChange={setOpp}
        />
      </div>

      <div style={{ height: 1, background: "rgba(255,255,255,0.07)", margin: "18px 0" }} />

      {ready ? (
        <>
          {yourPop !== oppPop ? (
            (() => {
              const youWin = yourPop > oppPop;
              const verdictColor = youWin ? WIN_COLOR : LOSE_COLOR;
              return (
                <ResultStrip
                  color={verdictColor}
                  badge={youWin ? "⚔️ YOU WIN" : "⚔️ YOU LOSE"}
                  text={`Higher pop wins the ${game === "PUBG" ? "PK" : "battle"} — you walk away with`}
                  formula={youWin
                    ? `${A} (yours) + ${B}/2 (looted) = ${A + B / 2}`
                    : `${A}/2 (you keep half) = ${A / 2}`}
                  value={youWin ? A + B / 2 : A / 2}
                />
              );
            })()
          ) : (
            <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
              <div style={{ fontSize: 12, fontWeight: 800, letterSpacing: 1.2, color: "#ffd166" }}>
                ⚖️ DEAD EVEN — identical popularity, either side can take it
              </div>
              <ResultStrip
                color={WIN_COLOR}
                badge="IF YOU WIN"
                text="Your points plus half of theirs"
                formula={`${A} (yours) + ${B}/2 (looted) = ${A + B / 2}`}
                value={A + B / 2}
              />
              <ResultStrip
                color={LOSE_COLOR}
                badge="IF YOU LOSE"
                text="You keep half of your own"
                formula={`${A}/2 = ${A / 2}`}
                value={A / 2}
              />
            </div>
          )}

          {yourPop < oppPop && (() => {
            const overtake = oppPop + 1 - yourPop;
            const newA = POP_POINT_TABLE[tierIndexOf(oppPop + 1)].points;
            return (
              <div style={{ marginTop: 12, fontSize: 12, color: "#a4a7b0", lineHeight: 1.6 }}>
                💡 Gain <strong style={{ color: accent }}>+{fmtPop(overtake)} pop</strong>{" "}
                before the battle to overtake them — you'd flip it and win{" "}
                <strong style={{ color: WIN_COLOR, fontFamily: NUMFONT, fontSize: 14 }}>
                  {newA + B / 2} pts
                </strong>{" "}
                instead of keeping {A / 2}.
              </div>
            );
          })()}
          {yourPop > oppPop && oppNext && POP_POINT_TABLE[oi].to + 1 <= yourPop && (
            <div style={{ marginTop: 12, fontSize: 12, color: "#a4a7b0", lineHeight: 1.6 }}>
              💡 If your opponent gains{" "}
              <strong style={{ color: accent }}>+{fmtPop(POP_POINT_TABLE[oi].to + 1 - oppPop)} pop</strong>{" "}
              (still below you), your win grows to{" "}
              <strong style={{ color: WIN_COLOR, fontFamily: NUMFONT, fontSize: 14 }}>
                {A + oppNext.points / 2} pts
              </strong>{" "}
              (+{(oppNext.points - B) / 2}) — bigger opponents pay bigger loot.
            </div>
          )}
        </>
      ) : (
        <div style={{ fontSize: 13, color: "#7a7d86", textAlign: "center", padding: "10px 0" }}>
          Fill in both popularities to see your win / loss points.
        </div>
      )}
    </Card>
  );
}

// ---------- target mode ----------

function TargetFinder({ accent }) {
  const [raw, setRaw] = useState("50");
  const target = parseInt(String(raw).replace(/\D/g, ""), 10) || 0;

  const { winRows, beatAnyoneTier, lossTier } = useMemo(() => {
    // Goal: score AT LEAST `target` in one battle, spending the least pop on
    // both sides. Higher pop wins, so you can only beat your tier or below
    // (bi <= ai). For each of your tiers (ascending) find the cheapest
    // opponent tier that clears the target; once a tier clears it against
    // the lowest opponent tier, every tier above does too — collapse those
    // into a single "beat anyone" row.
    if (target <= 0 || target > 66) return { winRows: [], beatAnyoneTier: null, lossTier: null };
    const rows = [];
    let anyone = null;
    for (let ai = 0; ai < POP_POINT_TABLE.length; ai++) {
      const a = POP_POINT_TABLE[ai];
      let found = null;
      for (let bi = 0; bi <= ai; bi++) {
        const b = POP_POINT_TABLE[bi];
        if (a.points + b.points / 2 >= target) { found = { b, bi }; break; }
      }
      if (!found) continue;
      if (found.bi === 0) { anyone = a; break; }
      rows.push({ a, b: found.b, sameTier: found.bi === ai, score: a.points + found.b.points / 2 });
    }
    const lossTier = POP_POINT_TABLE.find((t) => t.points / 2 >= target) || null;
    return { winRows: rows, beatAnyoneTier: anyone, lossTier };
  }, [target]);

  const seasonPlan = useMemo(() => {
    if (target <= 66) return null;
    // Same-tier win banks 1.5× your base points per battle.
    return [POP_POINT_TABLE[3], POP_POINT_TABLE[4], POP_POINT_TABLE[9], POP_POINT_TABLE[11]].map((t) => ({
      tier: t,
      perWin: t.points * 1.5,
      wins: Math.ceil(target / (t.points * 1.5)),
    }));
  }, [target]);

  return (
    <Card accent={accent}>
      <div style={{ display: "flex", gap: 16, flexWrap: "wrap", alignItems: "center" }}>
        <div style={{ flex: "0 0 auto" }}>
          <div style={{ fontSize: 11, fontWeight: 900, letterSpacing: 1.6, color: accent, marginBottom: 5 }}>
            TARGET POINTS
          </div>
          <input
            value={raw}
            onChange={(e) => setRaw(e.target.value)}
            placeholder="50"
            inputMode="numeric"
            style={{
              width: 96, boxSizing: "border-box",
              padding: "9px 12px", borderRadius: 11,
              background: "rgba(0,0,0,0.35)",
              border: `1px solid ${accent}66`,
              color: "#fff", fontSize: 22, fontWeight: 700, fontFamily: NUMFONT,
              letterSpacing: 1, outline: "none", textAlign: "center",
            }}
          />
        </div>
        <div style={{ flex: "1 1 220px", minWidth: 0, fontSize: 12.5, color: "#a4a7b0", lineHeight: 1.6 }}>
          {target > 0 && target <= 66 ? (
            <>
              Cheapest ways to score at least{" "}
              <strong style={{ color: "#fff", fontFamily: NUMFONT, fontSize: 15 }}>{target} pts</strong>{" "}
              in one battle — minimum pop on both sides.
            </>
          ) : target > 66 ? (
            <>Above the single-battle max of <strong style={{ color: "#fff", fontFamily: NUMFONT, fontSize: 15 }}>66 pts</strong> — showing a season plan instead.</>
          ) : (
            <>Enter the points you need — the finder lists the lowest-pop matchups that reach it.</>
          )}
        </div>
      </div>

      {target > 0 && (
        <div style={{ height: 1, background: "rgba(255,255,255,0.07)", margin: "16px 0" }} />
      )}

      {target > 0 && target <= 66 && (
        <>
          <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
            {winRows.map(({ a, b, sameTier, score }, i) => (
              <div
                key={`w${i}`}
                style={{
                  display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap",
                  padding: "10px 13px", borderRadius: 12,
                  background: "rgba(255,255,255,0.03)", border: `1px solid ${WIN_COLOR}33`,
                }}
              >
                <span style={{ fontSize: 10, fontWeight: 900, letterSpacing: 1.6, color: WIN_COLOR, background: `${WIN_COLOR}1A`, padding: "4px 9px", borderRadius: 999 }}>
                  WIN
                </span>
                <span style={{ fontSize: 13, color: "#eaeaef" }}>
                  You at <strong style={{ color: accent }}>{minPopLabel(a)}</strong> pop ({a.points} pts)
                  {" beat "}
                  <strong style={{ color: accent }}>{minPopLabel(b)}</strong> pop ({b.points} pts)
                  {sameTier && (
                    <span style={{ color: "#7a7d86", fontSize: 11.5 }}> · same tier — your pop must edge theirs</span>
                  )}
                </span>
                <span style={{ marginLeft: "auto", fontFamily: MONO, fontSize: 12, color: "#8f93a0", whiteSpace: "nowrap" }}>
                  {a.points} + {b.points / 2} = {score}
                </span>
              </div>
            ))}
            {beatAnyoneTier && (
              <div
                style={{
                  display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap",
                  padding: "10px 13px", borderRadius: 12,
                  background: "rgba(255,255,255,0.03)", border: `1px solid ${WIN_COLOR}33`,
                }}
              >
                <span style={{ fontSize: 10, fontWeight: 900, letterSpacing: 1.6, color: WIN_COLOR, background: `${WIN_COLOR}1A`, padding: "4px 9px", borderRadius: 999 }}>
                  WIN
                </span>
                <span style={{ fontSize: 13, color: "#eaeaef" }}>
                  You at <strong style={{ color: accent }}>{minPopLabel(beatAnyoneTier)}</strong> pop
                  ({beatAnyoneTier.points} pts) or above —{" "}
                  <strong style={{ color: WIN_COLOR }}>beat anyone</strong>
                </span>
                <span style={{ marginLeft: "auto", fontFamily: MONO, fontSize: 12, color: "#8f93a0", whiteSpace: "nowrap" }}>
                  {beatAnyoneTier.points} + 3 = {beatAnyoneTier.points + 3}+
                </span>
              </div>
            )}
            {lossTier && (
              <div
                style={{
                  display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap",
                  padding: "10px 13px", borderRadius: 12,
                  background: "rgba(255,255,255,0.03)", border: `1px solid ${LOSE_COLOR}33`,
                }}
              >
                <span style={{ fontSize: 10, fontWeight: 900, letterSpacing: 1.6, color: LOSE_COLOR, background: `${LOSE_COLOR}1A`, padding: "4px 9px", borderRadius: 999 }}>
                  EVEN A LOSS
                </span>
                <span style={{ fontSize: 13, color: "#eaeaef" }}>
                  Lose at <strong style={{ color: accent }}>{minPopLabel(lossTier)}</strong> pop
                  ({lossTier.points} pts) — you keep half
                </span>
                <span style={{ marginLeft: "auto", fontFamily: MONO, fontSize: 12, color: "#8f93a0", whiteSpace: "nowrap" }}>
                  {lossTier.points}/2 = {lossTier.points / 2}
                </span>
              </div>
            )}
          </div>
        </>
      )}

      {target > 66 && seasonPlan && (
        <>
          <div style={{ fontSize: 12.5, color: "#c9cdd6", lineHeight: 1.7, marginBottom: 12 }}>
            The biggest single-battle haul is{" "}
            <strong style={{ color: WIN_COLOR, fontFamily: NUMFONT, fontSize: 15 }}>66 pts</strong>{" "}
            (4M+ beats 4M+), so <strong style={{ fontFamily: NUMFONT, fontSize: 15 }}>{target} pts</strong> is
            banked over the season. Winning against your own tier earns 1.5× your base per battle:
          </div>
          <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
            {seasonPlan.map(({ tier, perWin, wins }) => (
              <div
                key={tier.points}
                style={{
                  flex: "1 1 150px", padding: "12px 14px", borderRadius: 12,
                  background: "rgba(255,255,255,0.03)", border: `1px solid ${accent}33`,
                }}
              >
                <div style={{ fontFamily: MONO, fontSize: 11, color: "#a4a7b0" }}>
                  AT {minPopLabel(tier)} POP
                </div>
                <div style={{ fontFamily: NUMFONT, fontSize: 30, fontWeight: 700, color: accent, lineHeight: 1.1, marginTop: 4 }}>
                  {wins} <span style={{ fontSize: 13, color: "#a4a7b0" }}>wins</span>
                </div>
                <div style={{ fontSize: 11, color: "#7a7d86", marginTop: 3 }}>
                  {perWin} pts per same-tier win
                </div>
              </div>
            ))}
          </div>
          <div style={{ fontSize: 11, color: "#7a7d86", marginTop: 10 }}>
            Beating higher-tier opponents banks more per win — use the calculator tab to test a matchup.
          </div>
        </>
      )}
    </Card>
  );
}

// ---------- strategy mode ----------

// Hand-verified 10-round season routes that land EXACTLY on the level
// threshold. Constraints agreed with the strategy: opponents never
// below the 2.5K (10-pt) tier, never your own tier — every win is
// structurally guaranteed because the opponent's whole tier sits
// below your pop — and your own pop stays within the 500K–900K tier.
// Wins pay base + half the opponent's base; losses pay half your base.
const sw = (you, base, opp, loot, push) => ({ you, base, opp, loot, pts: base + loot, push });
const sl = (you, base) => ({ you, base, pts: base / 2, loss: true });

const STRAT_COLS = "48px 64px 1.1fr 1.3fr 92px 100px";

const SEASON_PLANS = {
  370: [
    {
      name: "10 WINS · 0 LOSSES",
      home: "120K",
      note: "Start at 50K, gift-push to 120K before round 3. Rounds 8–10 are calibrated small wins that land the total exactly.",
      rounds: [
        sw("50K", 24, "15K", 10), sw("50K", 24, "15K", 10),
        sw("120K", 28, "50K", 12, "120K"), sw("120K", 28, "50K", 12),
        sw("120K", 28, "50K", 12), sw("120K", 28, "50K", 12), sw("120K", 28, "50K", 12),
        sw("120K", 28, "2.5K", 5), sw("120K", 28, "2.5K", 5), sw("120K", 28, "8K", 8),
      ],
    },
    {
      name: "8 WINS · 2 LOSSES",
      home: "260K",
      note: "Sit at 260K all season. Two losses are budgeted — they can land in any round.",
      rounds: [
        sw("260K", 32, "15K", 10), sw("260K", 32, "15K", 10), sw("260K", 32, "15K", 10), sw("260K", 32, "15K", 10),
        sl("260K", 32),
        sw("260K", 32, "15K", 10), sw("260K", 32, "15K", 10), sw("260K", 32, "15K", 10),
        sw("260K", 32, "50K", 12),
        sl("260K", 32),
      ],
    },
    {
      name: "6 WINS · 4 LOSSES",
      home: "500K",
      note: "The loss-tolerant route at the 500K–900K cap — four losses absorbed and the total still lands exactly.",
      rounds: [
        sw("500K", 36, "120K", 14), sw("500K", 36, "120K", 14),
        sl("500K", 36),
        sw("500K", 36, "120K", 14),
        sl("500K", 36),
        sw("500K", 36, "120K", 14),
        sl("500K", 36),
        sw("500K", 36, "120K", 14), sw("500K", 36, "50K", 12),
        sl("500K", 36),
      ],
    },
  ],
  300: [
    {
      name: "10 WINS · 0 LOSSES",
      home: "50K",
      note: "The low-pop route: start at just 15K, push to 50K before round 6.",
      rounds: [
        sw("15K", 20, "5K", 7), sw("15K", 20, "5K", 7), sw("15K", 20, "5K", 7),
        sw("15K", 20, "5K", 7), sw("15K", 20, "5K", 7),
        sw("50K", 24, "15K", 10, "50K"), sw("50K", 24, "15K", 10),
        sw("50K", 24, "15K", 10), sw("50K", 24, "15K", 10),
        sw("50K", 24, "2.5K", 5),
      ],
    },
    {
      name: "6 WINS · 4 LOSSES",
      home: "260K",
      note: "260K with four losses to spare — small guaranteed wins do the trimming.",
      rounds: [
        sw("260K", 32, "2.5K", 5), sw("260K", 32, "2.5K", 5),
        sl("260K", 32),
        sw("260K", 32, "50K", 12),
        sl("260K", 32),
        sw("260K", 32, "2.5K", 5),
        sl("260K", 32),
        sw("260K", 32, "50K", 12), sw("260K", 32, "2.5K", 5),
        sl("260K", 32),
      ],
    },
  ],
};

function StrategyView({ accent, game }) {
  const [targetPts, setTargetPts] = useState(370);
  const lvlName = (n) => (game === "PUBG" ? `PK${n}` : `Battle ${n}`);
  const targets = [
    { pts: 370, level: 10, icon: "🏆" },
    { pts: 300, level: 8, icon: "🥇" },
  ];

  return (
    <Card accent={accent}>
      <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginBottom: 10 }}>
        {targets.map((t) => {
          const active = targetPts === t.pts;
          return (
            <button
              key={t.pts}
              onClick={() => setTargetPts(t.pts)}
              style={{
                padding: "8px 16px", borderRadius: 999, cursor: "pointer",
                border: active ? `1.5px solid ${accent}` : "1px solid rgba(255,255,255,0.12)",
                background: active ? `${accent}22` : "rgba(255,255,255,0.04)",
                color: active ? accent : "#a4a7b0",
                fontSize: 13, fontWeight: 800, letterSpacing: 0.6,
                boxShadow: active ? `0 0 14px ${accent}33` : "none",
                transition: "all 0.15s ease",
              }}
            >
              {t.icon} {lvlName(t.level)} · {t.pts} pts
            </button>
          );
        })}
      </div>

      <div
        style={{
          padding: "10px 14px", borderRadius: 10, marginBottom: 16,
          background: "rgba(255,255,255,0.03)", border: "1px solid rgba(255,255,255,0.07)",
          fontSize: 11.5, color: "#a4a7b0", lineHeight: 1.7,
        }}
      >
        <div style={{ fontSize: 10.5, fontWeight: 900, letterSpacing: 1.6, color: "#c9cdd6", marginBottom: 4 }}>
          📜 SCORING RULES
        </div>
        · Each route lands <strong style={{ color: "#fff" }}>exactly on {targetPts} pts</strong> over the 10 season rounds.<br />
        · <strong style={{ color: WIN_COLOR }}>WIN</strong> pays your base + half the opponent's base ·{" "}
        <strong style={{ color: LOSE_COLOR }}>LOSS</strong> pays half your base.<br />
        · The opponent column is the tier to challenge — their whole tier sits <em>below</em> your pop, so every listed win is guaranteed.<br />
        · <strong style={{ color: accent }}>▲ push</strong> = raise your pop (gifts) to that value <em>before</em> the round.
      </div>

      {SEASON_PLANS[targetPts].map((plan) => {
        let running = 0;
        const rows = plan.rounds.map((r) => ({ ...r, total: (running += r.pts) }));
        const winCount = plan.rounds.filter((r) => !r.loss).length;
        const lossCount = plan.rounds.length - winCount;
        return (
          <div key={plan.name} style={{ marginBottom: 20 }}>
            <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap", marginBottom: 8 }}>
              <span
                style={{
                  padding: "5px 12px", borderRadius: 999,
                  background: `${WIN_COLOR}1F`, border: `1px solid ${WIN_COLOR}66`,
                  color: WIN_COLOR, fontSize: 11, fontWeight: 900, letterSpacing: 1.4,
                }}
              >
                {winCount} WINS
              </span>
              {lossCount > 0 && (
                <span
                  style={{
                    padding: "5px 12px", borderRadius: 999,
                    background: `${LOSE_COLOR}1A`, border: `1px solid ${LOSE_COLOR}55`,
                    color: LOSE_COLOR, fontSize: 11, fontWeight: 900, letterSpacing: 1.4,
                  }}
                >
                  {lossCount} LOSSES
                </span>
              )}
              <span style={{ fontFamily: MONO, fontSize: 11, color: "#a4a7b0", letterSpacing: 0.5 }}>
                HOME TIER {plan.home}
              </span>
            </div>
            <div style={{ fontSize: 11.5, color: "#8f93a0", lineHeight: 1.55, marginBottom: 8 }}>{plan.note}</div>

            <div style={{ overflowX: "auto" }}>
              <div style={{ minWidth: 600, borderRadius: 12, border: "1px solid rgba(255,255,255,0.09)", overflow: "hidden" }}>
                <div
                  style={{
                    display: "grid", gridTemplateColumns: STRAT_COLS, gap: 10,
                    padding: "8px 14px", alignItems: "center",
                    background: "rgba(255,255,255,0.05)",
                  }}
                >
                  {["ROUND", "RESULT", "YOUR POP (BASE)", "OPPONENT TO PICK (BASE)", "PTS EARNED", "SEASON TOTAL"].map((h, i) => (
                    <span
                      key={h}
                      style={{
                        fontSize: 9.5, fontWeight: 900, letterSpacing: 1.3, color: "#8f93a0",
                        textAlign: i >= 4 ? "right" : "left",
                      }}
                    >
                      {h}
                    </span>
                  ))}
                </div>
                {rows.map((r, i) => {
                  const last = i === rows.length - 1;
                  const color = r.loss ? LOSE_COLOR : WIN_COLOR;
                  return (
                    <div
                      key={i}
                      style={{
                        display: "grid", gridTemplateColumns: STRAT_COLS, gap: 10,
                        padding: "8px 14px", alignItems: "center",
                        background: last
                          ? `linear-gradient(90deg, ${WIN_COLOR}12, rgba(0,0,0,0))`
                          : i % 2 ? "rgba(255,255,255,0.025)" : "transparent",
                        borderTop: "1px solid rgba(255,255,255,0.05)",
                      }}
                    >
                      <span style={{ fontFamily: NUMFONT, fontSize: 15, fontWeight: 700, color: "#c8ced5" }}>
                        {i + 1}
                      </span>
                      <span
                        style={{
                          fontSize: 9.5, fontWeight: 900, letterSpacing: 1.3, color,
                          background: `${color}1A`, border: `1px solid ${color}44`,
                          padding: "3px 0", borderRadius: 999, textAlign: "center",
                        }}
                      >
                        {r.loss ? "LOSS" : "WIN"}
                      </span>
                      <span style={{ fontSize: 12.5, color: "#eaeaef", minWidth: 0 }}>
                        {r.push && (
                          <span style={{ display: "block", fontSize: 10.5, fontWeight: 800, color: accent }}>
                            ▲ push to {r.push} first
                          </span>
                        )}
                        <strong>{r.you}</strong>
                        <span style={{ color: "#8f93a0" }}> · {r.base} pts</span>
                      </span>
                      <span style={{ fontSize: 12.5, color: "#eaeaef", minWidth: 0 }}>
                        {r.loss ? (
                          <span style={{ color: "#8f93a0" }}>Any higher-pop player</span>
                        ) : (
                          <>
                            <strong>{r.opp}</strong>
                            <span style={{ color: "#8f93a0" }}> tier · {r.loot * 2} pts</span>
                          </>
                        )}
                      </span>
                      <span style={{ textAlign: "right" }}>
                        <span style={{ display: "block", fontFamily: NUMFONT, fontSize: 17, fontWeight: 700, color, lineHeight: 1.1 }}>
                          +{r.pts}
                        </span>
                        <span style={{ fontFamily: MONO, fontSize: 10, color: "#7a7d86" }}>
                          {r.loss ? `${r.base} ÷ 2` : `${r.base} + ${r.loot}`}
                        </span>
                      </span>
                      <span
                        style={{
                          fontFamily: NUMFONT, fontSize: 17, fontWeight: 700, textAlign: "right",
                          color: last ? WIN_COLOR : "#eaeaef",
                          textShadow: last ? `0 0 12px ${WIN_COLOR}66` : "none",
                        }}
                      >
                        {r.total}{last && " ✓"}
                      </span>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        );
      })}

      <div style={{ fontSize: 11, color: "#7a7d86", lineHeight: 1.6 }}>
        Losses are position-flexible (only the win/loss count matters), but the specific opponent
        tiers are not — the small "trim" wins are what make the season total land exactly on{" "}
        {targetPts}. Overshooting is fine too: the threshold only needs to be reached.
      </div>
    </Card>
  );
}

// ---------- page ----------

export default function BattleCalculator() {
  const { game } = useGame();
  const accent = ACCENTS[game] || "#3498db";
  const [mode, setMode] = useState("calc");

  return (
    <div className="container" style={{ paddingBottom: 64, maxWidth: 860 }}>
      <header style={{ padding: "8px 0 24px", borderBottom: "1px solid var(--border)", marginBottom: 24 }}>
        <h1 style={{ margin: "0 0 6px", fontSize: 26, fontWeight: 800 }}>
          Pop Battle Point Calculator
        </h1>
        <p className="muted" style={{ margin: 0 }}>
          Know your exact 1v1 settlement before you queue — and how much pop
          buys the next tier for you or your opponent.
        </p>
      </header>

      <div style={{ display: "flex", justifyContent: "flex-start", marginBottom: 16 }}>
        <GamePicker />
      </div>

      {/* Mode switch — compact segmented tabs. */}
      <div className="bc-mode-tabs" style={{ display: "grid", gridTemplateColumns: "repeat(3, minmax(0, 1fr))", gap: 10, marginBottom: 18 }}>
        {[
          { key: "calc", icon: "🧮", label: "Matchup Calculator", sub: "Your pop vs theirs — settled points" },
          { key: "target", icon: "🎯", label: "Target Finder", sub: "Pick a score — cheapest ways to reach it" },
          { key: "strat", icon: "🏆", label: "Season Strategy", sub: `Exact routes to ${game === "PUBG" ? "PK8 & PK10" : "Battle 8 & 10"}` },
        ].map((t) => {
          const active = mode === t.key;
          return (
            <button
              key={t.key}
              onClick={() => setMode(t.key)}
              style={{
                display: "flex", alignItems: "center", gap: 10, textAlign: "left",
                padding: "10px 14px", borderRadius: 12, cursor: "pointer",
                border: active ? `1.5px solid ${accent}` : "1px solid rgba(255,255,255,0.1)",
                background: active
                  ? `linear-gradient(150deg, ${accent}26, ${accent}0A), #10141c`
                  : "rgba(255,255,255,0.03)",
                boxShadow: active ? `0 6px 20px ${accent}2E` : "none",
                transition: "all 0.15s ease",
              }}
            >
              <span
                style={{
                  width: 32, height: 32, borderRadius: 9, flexShrink: 0,
                  display: "flex", alignItems: "center", justifyContent: "center",
                  fontSize: 16,
                  background: active ? `linear-gradient(135deg, ${accent}, ${accent}99)` : "rgba(255,255,255,0.06)",
                  boxShadow: active ? `0 0 12px ${accent}55` : "none",
                }}
              >
                {t.icon}
              </span>
              <span style={{ minWidth: 0 }}>
                <span style={{ display: "block", fontSize: 13.5, fontWeight: 800, color: active ? "#fff" : "#a4a7b0" }}>
                  {t.label}
                </span>
                <span style={{ display: "block", fontSize: 10.5, color: active ? "#c9cdd6" : "#7a7d86", marginTop: 1 }}>
                  {t.sub}
                </span>
              </span>
            </button>
          );
        })}
      </div>

      {mode === "calc" && <ForwardCalc accent={accent} game={game} />}
      {mode === "target" && <TargetFinder accent={accent} />}
      {mode === "strat" && <StrategyView accent={accent} game={game} />}

      <style>{`
        @media (max-width: 640px) {
          .bc-mode-tabs { grid-template-columns: 1fr !important; }
        }
      `}</style>

      <div style={{ marginTop: 14, fontSize: 11.5, color: "#7a7d86", lineHeight: 1.6 }}>
        Winner = own base points + half the loser's · Loser keeps half their own.
        Base points come from your popularity tier at battle time — see the full
        tables on the <a href={`${import.meta.env.BASE_URL}`} style={{ color: accent }}>directory</a> or{" "}
        <a href={`${import.meta.env.BASE_URL}calendar`} style={{ color: accent }}>battle calendar</a>.
      </div>
    </div>
  );
}
